import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';
import { Championship, Car, Track } from '../types';
import ChampionshipDetailsTab from './ChampionshipDetailsTab';
import ChampionshipEventsTab from './ChampionshipEventsTab';
import ChampionshipEntryListTab from './ChampionshipEntryListTab';
import ChampionshipResultsTab from './ChampionshipResultsTab';
import './ChampionshipEditor.css';

type ChampionshipTabType = 'details' | 'events' | 'entrylist' | 'results';

interface ChampionshipEditorProps {
  championship: Championship | null;
  isNew: boolean;
  cars: Car[];
  tracks: Track[];
  activeTab?: ChampionshipTabType;
  onTabChange?: (tab: ChampionshipTabType) => void;
  onSave: (championship: Championship) => void;
  onCancel: () => void;
}

const ChampionshipEditor: React.FC<ChampionshipEditorProps> = ({
  championship,
  isNew,
  cars,
  tracks,
  activeTab: activeTabProp,
  onTabChange,
  onSave,
  onCancel,
}) => {
  const [internalTab, setInternalTab] = useState<ChampionshipTabType>('details');
  const activeTab = activeTabProp ?? internalTab;
  const setActiveTab = (tab: ChampionshipTabType) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalTab(tab);
    }
  };
  const [editedChampionship, setEditedChampionship] = useState<Championship | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (championship) {
      setEditedChampionship(championship);
      setHasChanges(false);
    } else if (isNew) {
      // Create new championship with defaults
      setEditedChampionship({
        id: '',
        name: '',
        communityAbbreviation: '',
        season: 1,
        recurrence: 'weekly',
        spots: 0,
        enableEntryList: false,
        enableRegistrations: false,
        events: [],
        createdAt: Date.now(),
      });
      setHasChanges(false);
    }
  }, [championship, isNew]);

  const handleSave = () => {
    if (editedChampionship) {
      onSave(editedChampionship);
      setHasChanges(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }
    onCancel();
  };

  const updateChampionship = (updates: Partial<Championship>) => {
    if (editedChampionship) {
      setEditedChampionship({ ...editedChampionship, ...updates });
      setHasChanges(true);
    }
  };

  if (!editedChampionship) {
    return (
      <div className="championship-editor-empty">
        <p>Select a championship to edit or create a new one</p>
      </div>
    );
  }

  const tabs = [
    { key: 'details', label: 'Details', disabled: false },
    { key: 'events', label: 'Events', disabled: false },
    { key: 'entrylist', label: 'Entry List', disabled: !editedChampionship.enableEntryList },
    { key: 'results', label: 'Results', disabled: isNew },
  ];

  return (
    <div className="championship-editor">
      <div className="championship-editor-header">
        <h2>{isNew ? 'New Championship' : editedChampionship.name}</h2>
        <div className="championship-editor-actions">
          <button className="cancel-button" onClick={handleCancel}>
            <X size={18} />
            Cancel
          </button>
          <button
            className="save-button"
            onClick={handleSave}
            disabled={!editedChampionship.name || !editedChampionship.communityAbbreviation}
          >
            <Save size={18} />
            {isNew ? 'Create Championship' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="championship-editor-tabs">
        <div className="tabs-header-wrapper">
        <div className="tabs-header">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
              onClick={() => !tab.disabled && setActiveTab(tab.key as typeof activeTab)}
              disabled={tab.disabled}
            >
              {tab.label}
              {tab.disabled && tab.key === 'entrylist' && ' (Enable in Details)'}
              {tab.disabled && tab.key === 'results' && ' (Save first)'}
            </button>
          ))}
        </div>
        </div>

        <div className="tabs-content">
          {activeTab === 'details' && (
            <ChampionshipDetailsTab
              championship={editedChampionship}
              cars={cars}
              tracks={tracks}
              onChange={updateChampionship}
            />
          )}
          {activeTab === 'events' && (
            <ChampionshipEventsTab
              championship={editedChampionship}
              tracks={tracks}
              cars={cars}
              onChange={updateChampionship}
            />
          )}
          {activeTab === 'entrylist' && (
            <ChampionshipEntryListTab
              championship={editedChampionship}
              onChange={updateChampionship}
              cars={cars}
            />
          )}
          {activeTab === 'results' && !isNew && (
            <ChampionshipResultsTab championshipId={editedChampionship.id} />
          )}
        </div>
      </div>

      {hasChanges && (
        <div className="unsaved-changes-indicator">
          <span>You have unsaved changes</span>
        </div>
      )}
    </div>
  );
};

export default ChampionshipEditor;
